import { IonInput } from "@ionic/react";
import { clsx } from "clsx";
import React, { ReactNode, useState } from "react";
import { Control, Controller } from "react-hook-form";

import { roundToMaxDecimals } from "@/utils/common";
import { fieldLabel } from "@/utils/form/labels";

export type ControlledInputProps = React.ComponentProps<typeof IonInput> & {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  label?: string;
  children?: ReactNode;
  className?: string;
  /**
   * When `type="number"`, form state keeps the raw number but the input shows this
   * formatted value (e.g. whole gallons while storing fractional gallons).
   * Number fields render as text with `inputMode` so mobile shows a numeric keypad.
   */
  formatNumberForDisplay?: (n: number) => string | number;
  /** Formats text input on each keystroke (e.g. US phone numbers). */
  formatValue?: (value: string) => string;
};

type ControlledInputFieldProps = Omit<
  ControlledInputProps,
  "name" | "control"
> & {
  invalid: boolean;
  isTouched: boolean;
  error?: { message?: string };
  onBlur: () => void;
  onChange: (value: string | number) => void;
  value: string | number | null | undefined;
};

function numberToEditString(value: ControlledInputFieldProps["value"]): string {
  // eslint-disable-next-line no-undefined
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "number" && Number.isNaN(value)) {
    return "";
  }
  return String(value);
}

function getNumberDisplayValue(
  value: ControlledInputFieldProps["value"],
  formatNumberForDisplay?: (n: number) => string | number,
): ControlledInputProps["value"] {
  if (
    formatNumberForDisplay &&
    typeof value === "number" &&
    Number.isFinite(value)
  ) {
    return formatNumberForDisplay(value);
  }
  return value;
}

const NUMBER_INPUT_PATTERN = /^-?\d*(?:[.,]\d*)?$/u;

function normalizeDecimalSeparators(raw: string): string {
  return raw.split(",").join(".");
}

function parseNumberInput(raw: string): number {
  return parseFloat(normalizeDecimalSeparators(raw));
}

function getMaxDecimalPlaces(step: ControlledInputProps["step"]): number | null {
  // eslint-disable-next-line no-undefined
  if (step === null || step === undefined) {
    return null;
  }
  const stepStr = normalizeDecimalSeparators(String(step).trim());
  const dotIndex = stepStr.indexOf(".");
  if (dotIndex === -1) {
    return 0;
  }
  return stepStr.length - dotIndex - 1;
}

function constrainNumberInput(
  raw: string,
  maxDecimals: number | null,
): string | null {
  if (raw === "" || raw === "-") {
    return raw;
  }
  if (!NUMBER_INPUT_PATTERN.test(raw)) {
    return null;
  }
  const normalized = normalizeDecimalSeparators(raw);
  if (maxDecimals === null || normalized.endsWith(".")) {
    return raw;
  }
  const [, decimalPart = ""] = normalized.split(".");
  if (decimalPart.length <= maxDecimals) {
    return raw;
  }
  const parsed = parseNumberInput(raw);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return String(roundToMaxDecimals(parsed, maxDecimals));
}

function roundingNotice(
  maxDecimals: number,
  normalized: string,
): string {
  if (maxDecimals === 0) {
    return `Rounded to whole number: ${normalized}`;
  }
  return `Rounded to ${maxDecimals} decimal places: ${normalized}`;
}

function getRoundNotice(
  raw: string,
  normalized: string,
  maxDecimals: number | null,
): string | null {
  if (
    maxDecimals === null ||
    normalized === "" ||
    normalized === "-" ||
    normalized === raw ||
    normalized === normalizeDecimalSeparators(raw)
  ) {
    return null;
  }
  return roundingNotice(maxDecimals, normalized);
}

function getNumberInputMode(
  step: ControlledInputProps["step"],
  inputMode?: ControlledInputProps["inputMode"],
): ControlledInputProps["inputMode"] {
  if (inputMode) {
    return inputMode;
  }
  return getMaxDecimalPlaces(step) === 0 ? "numeric" : "decimal";
}

function processNumberBlur({
  editingValue,
  maxDecimals,
  onChange,
}: {
  editingValue: string | null;
  maxDecimals: number | null;
  onChange: (value: number) => void;
}): void {
  if (
    maxDecimals === null ||
    editingValue === null ||
    editingValue === "" ||
    editingValue === "-"
  ) {
    return;
  }
  const parsed = parseNumberInput(editingValue);
  if (!Number.isFinite(parsed)) {
    return;
  }
  const rounded = roundToMaxDecimals(parsed, maxDecimals);
  if (rounded !== parsed) {
    onChange(rounded);
  }
}

function ControlledInputField({
  onChange,
  onBlur,
  value,
  invalid,
  isTouched,
  error,
  className,
  labelPlacement = "stacked",
  label,
  required = false,
  type,
  step,
  formatNumberForDisplay,
  formatValue,
  children,
  helperText,
  inputMode,
  ...rest
}: ControlledInputFieldProps): ReactNode {
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [roundNotice, setRoundNotice] = useState<string | null>(null);
  const isNumberInput = type === "number";

  let displayValue = value;
  if (isNumberInput && editingValue !== null) {
    displayValue = editingValue;
  } else if (isNumberInput) {
    displayValue = getNumberDisplayValue(value, formatNumberForDisplay);
  }

  return (
    <IonInput
      {...rest}
      className={clsx(className, {
        "ion-invalid": invalid,
        "ion-touched": isTouched || error,
      })}
      labelPlacement={labelPlacement}
      // eslint-disable-next-line no-undefined
      label={label ? fieldLabel(label, required) : undefined}
      value={displayValue ?? ""}
      onIonFocus={() => {
        if (isNumberInput) {
          setEditingValue(numberToEditString(value));
          setRoundNotice(null);
        }
      }}
      onIonInput={(event) => {
        const raw = event.detail.value ?? "";
        if (isNumberInput) {
          const maxDecimals = getMaxDecimalPlaces(step);
          const normalized = constrainNumberInput(raw, maxDecimals);
          if (normalized === null) {
            return;
          }
          setRoundNotice(getRoundNotice(raw, normalized, maxDecimals));
          setEditingValue(normalized);
          onChange(
            normalized === "" || normalized === "-"
              ? NaN
              : parseNumberInput(normalized),
          );
          return;
        }
        if (formatValue) {
          onChange(formatValue(raw));
          return;
        }
        onChange(raw);
      }}
      onIonBlur={() => {
        if (isNumberInput) {
          processNumberBlur({
            editingValue,
            maxDecimals: getMaxDecimalPlaces(step),
            onChange,
          });
          setEditingValue(null);
          setRoundNotice(null);
        }
        onBlur();
      }}
      errorText={error?.message}
      helperText={roundNotice ?? helperText}
      required={required}
      type={isNumberInput ? "text" : type}
      inputMode={isNumberInput ? getNumberInputMode(step, inputMode) : inputMode}
      step={step}
    >
      {children}
    </IonInput>
  );
}

function ControlledInput({
  name,
  control,
  ...fieldProps
}: ControlledInputProps): ReactNode {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { invalid, isTouched, error },
      }) => (
        <ControlledInputField
          {...fieldProps}
          invalid={invalid}
          isTouched={isTouched}
          error={error}
          onBlur={onBlur}
          onChange={onChange}
          value={value}
        />
      )}
    />
  );
}

export { ControlledInput };
